/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { CSSProperties, ReactNode } from 'react';
import * as Icons from '@tishman/icons';

import { Box } from '../Box';
import { Button } from '../Button';

import { useCarouselContextV2 } from './CarouselV2ContextProvider';

interface CarouselNavigationV2Props {
  /** Custom styles for the navigation container */
  containerStyle?: CSSProperties;
  /** Custom styles for the navigation buttons */
  buttonStyle?: CSSProperties;
  /** Custom styles for disabled buttons */
  disabledButtonStyle?: CSSProperties;
  /** Custom previous button content */
  prevButtonContent?: ReactNode;
  /** Custom next button content */
  nextButtonContent?: ReactNode;
}

/**
 * CarouselNavigation - Custom navigation component for CarouselV2
 *
 * Can be placed anywhere in the component tree as long as it's
 * a child of CarouselV2
 */
export const CarouselNavigationV2 = ({
  containerStyle = {},
  prevButtonContent = (
    <React.Fragment>
      <Icons.Arrow
        aria-hidden
        style={{
          transition: 'transform 0.3s ease-in-out',
          transform: `scale(${24 / 18}) rotate(180deg)`,
        }}
      />
    </React.Fragment>
  ),
  nextButtonContent = (
    <React.Fragment>
      <Icons.Arrow
        aria-hidden
        style={{
          transition: 'transform 0.3s ease-in-out',
          transform: `scale(${24 / 18})`,
        }}
      />
    </React.Fragment>
  ),
}: CarouselNavigationV2Props) => {
  const { isBeginning, isEnd, goPrev, goNext } = useCarouselContextV2();

  if (isBeginning && isEnd) return null;

  return (
    <Box style={containerStyle} sx={{}}>
      <Button
        data-element="CarouselNavigationPrevious"
        disabled={isBeginning}
        onClick={goPrev}
        sx={{
          'cursor': 'pointer',
          ':not(:disabled):hover svg': {
            transform: `translateX(-5px) scale(${24 / 18}) rotate(180deg)`,
          },
        }}
        tabIndex={0}
        variant="icon"
      >
        {prevButtonContent}
      </Button>
      <Button
        data-element="CarouselNavigationNext"
        disabled={isEnd}
        onClick={goNext}
        sx={{
          'cursor': 'pointer',
          ':not(:disabled):hover svg': {
            transform: `translateX(5px) scale(${24 / 18}) `,
          },
        }}
        tabIndex={0}
        variant="icon"
      >
        {nextButtonContent}
      </Button>
    </Box>
  );
};
